<template>
    <OpportunityPageDetailTopLayout class="flex-1" :hide-line-break="isPersonalProfile" :minified="minified">
        <template #title="{ decorative }">
            <CHeading
                :class="minified ? 'mb-2' : 'sm:mb-4 mb-2 sm:block'"
                :tag="decorative ? 'div' : headingTag"
                :variant="minified ? '3' : '2'"
            >
                {{ fullName }}
            </CHeading>
        </template>

        <template #image>
            <CThumbnailImage
                :alt="fullName"
                :class="minified ? 'rounded-full h-[88px] w-[88px]' : 'rounded-md'"
                :dummy="profile.dummy"
                :src="isAnonymous ? undefined : profile.profileImg"
                type="profile"
            />
        </template>

        <template #highlights>
            <ProfileHighlights
                :contact-email="contactEmail"
                :contact-phone="contactPhone"
                :custom-location="customLocation"
                :is-anonymous="isAnonymous"
                :profile="profile"
            />
        </template>

        <template #description>
            <ProfileHighlightsDescription
                v-if="isTalentBaseProfile"
                :description="description"
                :description-title="'Čo hľadám'"
                :minified="minified"
            />
        </template>

        <template #interests>
            <div v-if="cInterests && cInterests.length" :class="isTalentBaseProfile ? 'sm:mt-12' : ''">
                <p :class="minified ? 'mb-1' : 'mb-1 sm:mb-2'">
                    <strong>{{ interestsTitle }}</strong>
                </p>
                <CTags :tags="cInterests" variant="white" @tag-click="handleTagClick" />
            </div>
        </template>

        <template v-if="isPersonalProfile && !preview" #side>
            <PersonalProfileSideButtons
                fixed-on-mobile
                :profile-id="profile.id"
                :profile-slug="profile.slug"
                :talent-base-profile-slug="profile.talentBaseUserProfile?.slug"
            />
        </template>
    </OpportunityPageDetailTopLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { EntityType } from '#shared/types/EntityType'
import type { UserProfile, UserProfileType } from '#shared/types/User'

interface Props {
    profile: UserProfile
    profileViewType: UserProfileType
    contactPhone?: string
    contactEmail?: string
    customDescription?: string
    interests?: string[]
    searchTagRouteName?: string
    searchTagQueryParam?: string
    customLocation?: string
    minified?: boolean
    preview?: boolean
    isAnonymous?: boolean
    /** Element for the name, e.g. `h1` when the profile is the page itself. Defaults to the variant's heading level. */
    headingTag?: string
}

const props = withDefaults(defineProps<Props>(), {
    contactPhone: undefined,
    contactEmail: undefined,
    customDescription: undefined,
    interests: () => [],
    searchTagRouteName: undefined,
    searchTagQueryParam: undefined,
    customLocation: undefined,
    minified: false,
    preview: false,
    isAnonymous: false,
    headingTag: ''
})

const isPersonalProfile = computed(() => {
    return props.profileViewType === EntityType.UserProfile
})

const isTalentBaseProfile = computed(() => {
    return props.profileViewType === EntityType.TalentBaseProfile
})

const description = computed(() => {
    return props.profile.talentBaseUserProfile?.why ?? ''
})

const cInterests = computed(() => {
    switch (props.profileViewType) {
        case EntityType.TalentBaseProfile:
            return props.profile.talentBaseUserProfile?.keywords ?? []
        case EntityType.Mentor:
            return props.profile.mentorProfile?.interests ?? []
        case EntityType.Volunteer:
            return props.profile.volunteerProfile?.interests ?? []
        default:
            return []
    }
})

const interestsTitle = computed(() => {
    switch (props.profileViewType) {
        case EntityType.TalentBaseProfile:
            return 'Kľúčové slová o práci'
        case EntityType.Mentor:
            return 'S čím viem ako mentor pomôcť'
        case EntityType.Volunteer:
            return 'S čím viem ako dobrovoľník pomôcť'
        default:
            return ''
    }
})

const fullName = computed(() => {
    return props.isAnonymous ? 'Anonymný profil' : `${props.profile.firstName} ${props.profile.lastName}`
})

function handleTagClick(tag: string) {
    const router = useRouter()
    
    // Navigate to mentori page with the clicked tag as an interests filter
    router.push({
        name: 'mentori',
        query: {
            interests: tag
        }
    })
}
</script>